/**
 * The entity catalog, read like shopping: where the business is (its country's own entities, toppings and names),
 * a category (the dish) or entities one by one, toppings on each, the order beside it all the way, then the review
 * (the server's dry run: exactly what will be written) and Add, in one commit.
 */
import { useEffect, useRef, useState } from "preact/hooks";
import { api } from "../../api/client.ts";
import type { CatalogOut, CategoryOut, TemplateOut, TemplatePlan } from "../../api/types.gen.ts";
import { endonym, LANGUAGES, locale, negotiate, t } from "../../i18n/index.ts";
import {
  addCategory,
  allOf,
  canReview,
  defaultGeography,
  flagged,
  hasCategory,
  type Pick,
  reconcile,
  removeCategory,
  request,
  search,
  searchCategories,
  setLink,
  toggle,
  toggleFacet,
  update,
} from "../../lib/catalog.ts";
import { useEntities } from "../../lib/data.ts";
import { spaceApi, spaceUrl } from "../../lib/paths.ts";
import { bump, useAction, useResource } from "../../lib/resource.ts";
import { canEditSchema } from "../../lib/roles.ts";
import { navigate } from "../../lib/router.ts";
import type { PageProps } from "../../lib/state.ts";
import { toast } from "../../lib/toast.ts";
import { useId } from "../../lib/uid.ts";
import { Button, Link } from "../../ui/Button.tsx";
import { EntityGlyph } from "../../ui/EntityGlyph.tsx";
import { Field, Input, Select, Switch } from "../../ui/form.tsx";
import { CatalogIcon, SearchIcon } from "../../ui/icons.tsx";
import { Async, EmptyState, Loading, Notice } from "../../ui/misc.tsx";
import { PageHeader } from "../../ui/PageHeader.tsx";
import { Steps } from "../../ui/Steps.tsx";
import { FOCUS, LINK } from "../../ui/styles.ts";
import { EntityCard } from "./card.tsx";
import { Order } from "./order.tsx";
import { Review } from "./review.tsx";

/** "International" in the geography picker. */
const NONE = "";
/** The cards. */
const GRID = "grid gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2";

function CategoryList(p: {
  categories: readonly CategoryOut[];
  entities: readonly TemplateOut[];
  current: string;
  onPick: (key: string) => void;
}) {
  const [q, setQ] = useState("");
  // The one being looked at stays, whatever the search.
  const found = searchCategories(p.categories, p.entities, q);
  const shown = p.categories.filter((c) => c.key === p.current || found.includes(c));
  const item = (key: string, label: string, icon: CategoryOut["icon"] | null) => (
    <li key={key} class="shrink-0">
      <button
        type="button"
        aria-pressed={p.current === key}
        onClick={() => p.onPick(key)}
        class={`flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-sm whitespace-nowrap transition-colors lg:whitespace-normal ${FOCUS} ${p.current === key ? "bg-blue-50 font-medium text-blue-700" : "text-zinc-700 hover:bg-zinc-950/5"}`}
      >
        {icon ? <EntityGlyph icon={icon} size={14} pad={3} plain={true} /> : <CatalogIcon class="m-[3px]" />}
        {label}
      </button>
    </li>
  );
  return (
    <nav aria-label={t("catalog.categories")} class="flex flex-col gap-2 lg:min-h-0">
      <div class="relative">
        <SearchIcon class="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-zinc-400" size={14} />
        <Input
          type="search"
          compact={true}
          value={q}
          onValue={setQ}
          aria-label={t("catalog.searchCategories")}
          placeholder={t("catalog.searchCategories")}
          class="w-full pl-8"
        />
      </div>
      {/* Room around its buttons, so their focus rings aren't cut by the scrolling. */}
      <ul class="-mx-4 flex gap-1 overflow-x-auto px-4 pb-2 lg:-m-1 lg:min-h-0 lg:flex-col lg:overflow-y-auto lg:overscroll-contain lg:p-1">
        {item("", t("catalog.all"), null)}
        {shown.map((c) => item(c.key, c.name, c.icon))}
      </ul>
      {found.length === 0 ? <p class="px-2.5 text-xs text-zinc-500">{t("catalog.noCategory")}</p> : null}
    </nav>
  );
}

function Browse(p: {
  catalog: CatalogOut;
  order: Pick[];
  setOrder: (o: Pick[]) => void;
  plan: TemplatePlan | undefined;
  category: string;
  setCategory: (c: string) => void;
}) {
  const [q, setQ] = useState("");
  const [others, setOthers] = useState(false);
  const othersHeading = useId();
  const cat = p.catalog.categories.find((c) => c.key === p.category);
  const inCategory = cat
    ? cat.entities.flatMap((x) => p.catalog.entities.filter((e) => e.key === x.key))
    : p.catalog.entities;
  const shown = search(inCategory, q);
  // Other countries' or regions' own entities, on request: those the category takes there, or all of them.
  const elsewhere = others
    ? search(cat ? p.catalog.others.filter((e) => cat.elsewhere?.includes(e.key)) : p.catalog.others, q)
    : [];
  const geoName = (g: string | null | undefined) => p.catalog.geographies.find((x) => x.key === g)?.name;
  const card = (e: TemplateOut) => {
    const pick = p.order.find((o) => o.key === e.key);
    const chosen = cat?.entities.find((x) => x.key === e.key);
    return (
      <EntityCard
        key={e.key}
        entity={e}
        pick={pick}
        geography={geoName(e.geography)}
        geoName={(g) => geoName(g) ?? g}
        hub={chosen?.hub ?? false}
        onToggle={() => p.setOrder(toggle(p.order, e, chosen?.facets))}
        onFacet={(f) => p.setOrder(toggleFacet(pick ? p.order : toggle(p.order, e, chosen?.facets), e.key, f))}
      />
    );
  };
  return (
    <div class="grid min-w-0 gap-4 lg:grid-cols-[12rem_minmax(0,1fr)] lg:gap-6">
      {/* Taller than the window (the categories are many): the list scrolls on its own, beside the entities. */}
      <div class="min-w-0 lg:sticky lg:top-4 lg:flex lg:max-h-[calc(100dvh-2rem)] lg:flex-col lg:self-start">
        <CategoryList
          categories={p.catalog.categories}
          entities={p.catalog.entities}
          current={p.category}
          onPick={p.setCategory}
        />
      </div>
      <div class="flex min-w-0 flex-col gap-4">
        {cat ? (
          <div class="flex flex-wrap items-start justify-between gap-3 rounded-lg bg-zinc-50 px-4 py-3">
            <div class="min-w-0 flex-1">
              <h2 class="font-medium text-zinc-900">{cat.name}</h2>
              <p class="text-sm text-zinc-600">{cat.description}</p>
            </div>
            {hasCategory(p.order, cat) ? (
              <Button size="sm" onClick={() => p.setOrder(removeCategory(p.order, cat))}>
                {t("catalog.removeAll")}
              </Button>
            ) : (
              <Button size="sm" variant="primary" onClick={() => p.setOrder(addCategory(p.order, cat))}>
                {t("catalog.addAll", { count: cat.entities.length })}
              </Button>
            )}
          </div>
        ) : null}
        <div class="relative">
          <SearchIcon class="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-zinc-400" />
          <Input
            type="search"
            value={q}
            onValue={setQ}
            aria-label={t("catalog.search")}
            placeholder={t("catalog.search")}
            class="pl-9"
          />
        </div>
        {shown.length === 0 && elsewhere.length === 0 ? (
          <EmptyState title={t("catalog.noMatch")}>{t("catalog.noMatchHint")}</EmptyState>
        ) : (
          <div class={GRID}>{shown.map(card)}</div>
        )}
        {p.catalog.others.length > 0 ? (
          <Switch checked={others} onCheck={setOthers} label={t("catalog.showOthers")} />
        ) : null}
        {elsewhere.length > 0 ? (
          <section aria-labelledby={othersHeading} class="flex flex-col gap-3">
            <h2 id={othersHeading} class="font-medium text-zinc-900">
              {t("catalog.others")}
            </h2>
            <div class={GRID}>{elsewhere.map(card)}</div>
          </section>
        ) : null}
      </div>
    </div>
  );
}

export function Catalog({ space }: PageProps) {
  const existing = useEntities(space);
  // The entities' language: the app's, unless the business works in another one.
  const [lang, setLang] = useState(() => negotiate([locale.value]));
  // Undefined until the catalog says which geographies there are; then the space's zone or the language picks one.
  const [geo, setGeo] = useState<string | undefined>(undefined);
  const [order, setOrder] = useState<Pick[]>([]);
  const [category, setCategory] = useState("");
  const [step, setStep] = useState<"browse" | "review">("browse");
  const [english, setEnglish] = useState(false);
  const catalog = useResource(`templates:${geo ?? "?"}:${lang}`, (signal) =>
    api.get<CatalogOut>("/templates", { language: lang, ...(geo && { geography: geo }) }, signal),
  );
  const shown = useRef<CatalogOut | undefined>(undefined);
  useEffect(() => {
    const c = catalog.data;
    if (!c) {
      return;
    }
    if (geo === undefined) {
      setGeo(defaultGeography(space.time_zone, lang, c.geographies));
      return;
    }
    if ((c.geography ?? NONE) === geo && shown.current !== c) {
      setOrder((o) => reconcile(o, shown.current, c));
      shown.current = c;
    }
  }, [catalog.data, geo, lang, space.time_zone]);
  const body = request(order, { language: lang, geography: geo ?? NONE, dry: true, english });
  const preview = useResource(`preview:${space.id}:${JSON.stringify(body)}`, (signal) =>
    order.length === 0
      ? Promise.resolve(null)
      : api.post<TemplatePlan>(spaceApi(space, "/templates/apply"), body, { signal }),
  );
  const plan = preview.data ?? undefined;
  // While a change is previewed again, the page keeps showing the last plan: a card's choice (merge or keep apart),
  // the order's summary and the review stay where they are (no flicker, focus kept) instead of blinking out until
  // the new one is back. Going on (Review, Add) waits for the plan of the order as it is.
  const last = useRef<TemplatePlan | undefined>(undefined);
  if (plan) {
    last.current = plan;
  }
  const shownPlan = plan ?? (order.length > 0 ? last.current : undefined);
  const reviewable = canReview(order, plan, preview.error);
  const [add, busy] = useAction(async () => {
    const out = await api.post<TemplatePlan>(
      spaceApi(space, "/templates/apply"),
      request(order, { language: lang, geography: geo ?? NONE, dry: false, english }),
    );
    bump();
    const links = [{ label: t("catalog.toEntities"), href: spaceUrl(space, "/entities") }];
    if (out.commit) {
      links.unshift({ label: t("catalog.undo"), href: spaceUrl(space, `/history/${out.commit}`) });
    }
    toast(t("catalog.added", { count: out.entities.length }), "info", 12_000, links);
    navigate(spaceUrl(space, "/entities"));
  });
  const ready = catalog.data && geo !== undefined && (catalog.data.geography ?? NONE) === geo;
  return (
    <>
      <PageHeader title={t("catalog.title")} icon={<CatalogIcon size={20} />}>
        <Link href={spaceUrl(space, "/entities")} class={LINK}>
          {t("catalog.back")}
        </Link>
      </PageHeader>
      <Notice when={space.schema_locked}>{t("entities.locked")}</Notice>
      <Steps labels={[t("catalog.stepChoose"), t("catalog.stepReview")]} current={step === "browse" ? 0 : 1} />
      <Async r={catalog}>
        {(c) =>
          ready ? (
            step === "review" && shownPlan ? (
              <>
                <Review
                  space={space}
                  plan={shownPlan}
                  existing={existing.data ?? []}
                  entities={allOf(c)}
                  english={english}
                  actions={{
                    onLink: (e, f, on) => setOrder(setLink(order, e, f, on)),
                    onFacet: (e, f) => setOrder(toggleFacet(order, e, f)),
                    onAdd: (k) => {
                      const e = allOf(c).find((x) => x.key === k);
                      if (e && !order.some((o) => o.key === k)) {
                        setOrder(toggle(order, e));
                      }
                    },
                    onEnglish: setEnglish,
                  }}
                  error={preview.error}
                  buttons={
                    <>
                      <Button onClick={() => setStep("browse")}>{t("common.back")}</Button>
                      <Button
                        variant="primary"
                        busy={busy}
                        disabled={!(canEditSchema(space) && reviewable) || space.schema_locked}
                        onClick={() => add()}
                      >
                        {t("catalog.apply", { count: shownPlan.entities.length })}
                      </Button>
                    </>
                  }
                />
              </>
            ) : (
              <>
                <div class="mb-5 grid max-w-3xl gap-4 sm:grid-cols-2">
                  <Field label={t("catalog.geography")} hint={t("catalog.geographyHint")}>
                    <Select
                      value={geo ?? NONE}
                      onValue={setGeo}
                      options={[
                        { value: NONE, label: flagged(NONE, t("catalog.international")) },
                        ...c.geographies.map((g) => ({ value: g.key, label: flagged(g.key, g.name) })),
                      ]}
                    />
                  </Field>
                  <Field label={t("catalog.language")} hint={t("catalog.languageHint")}>
                    <Select
                      value={lang}
                      onValue={setLang}
                      options={LANGUAGES.map((l) => ({ value: l, label: endonym(l) }))}
                    />
                  </Field>
                </div>
                <div class="grid gap-6 pb-20 lg:grid-cols-[minmax(0,1fr)_18rem] lg:pb-0">
                  <Browse
                    catalog={c}
                    order={order}
                    setOrder={setOrder}
                    plan={shownPlan}
                    category={category}
                    setCategory={setCategory}
                  />
                  <aside class="lg:sticky lg:top-4 lg:self-start">
                    <Order
                      order={order}
                      entities={allOf(c)}
                      plan={shownPlan}
                      ready={reviewable}
                      error={preview.error}
                      onRemove={(k) => setOrder(order.filter((o) => o.key !== k))}
                      onCollision={(k, on) => setOrder(update(order, k, { onCollision: on }))}
                      onName={(k, name) => setOrder(update(order, k, { name: name || undefined }))}
                      onClear={() => setOrder([])}
                      onReview={() => setStep("review")}
                    />
                  </aside>
                </div>
                {order.length > 0 ? (
                  <div class="fixed inset-x-0 bottom-0 z-20 flex items-center justify-between gap-3 border-t border-zinc-200 bg-white px-4 py-3 lg:hidden">
                    <span class="text-sm text-zinc-700">{t("catalog.picked", { count: order.length })}</span>
                    <Button variant="primary" disabled={!reviewable} onClick={() => setStep("review")}>
                      {t("catalog.review")}
                    </Button>
                  </div>
                ) : null}
              </>
            )
          ) : (
            <Loading />
          )
        }
      </Async>
    </>
  );
}
